import { useState, useEffect } from 'react';
import { getHomeContentApi, updateHomeContentApi, resetHomeContentApi } from '../../../../api/apiServices';

export default function HomePageContentManager() {
  const [content, setContent] = useState(null);
  const [activeSection, setActiveSection] = useState('hero'); // 'hero' | 'whyUs' | 'testimonials' | 'ctaBanner'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  // Testimonial modal state
  const [editingTestimonialIndex, setEditingTestimonialIndex] = useState(null);
  const [testimonialModal, setTestimonialModal] = useState(false);
  const [tempTestimonial, setTempTestimonial] = useState({
    name: '',
    role: '',
    course: '',
    text: '',
    avatar: '',
    rating: 5
  });

  const loadContent = async () => {
    try {
      setLoading(true);
      const res = await getHomeContentApi();
      if (res && res.data) {
        setContent(res.data);
      }
    } catch (err) {
      console.warn("Could not fetch home content:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setFeedback({ type: '', message: '' });
      const res = await updateHomeContentApi(content);
      if (res && res.success) {
        setFeedback({
          type: 'success',
          message: 'Home page content updated and published live successfully!'
        });
        if (res.data) setContent(res.data);
      } else {
        setFeedback({ type: 'error', message: res?.message || 'Failed to save changes.' });
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Error saving changes.' });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all home page content back to the default platform blueprint?')) return;
    try {
      setSaving(true);
      const res = await resetHomeContentApi();
      if (res && res.success) {
        setContent(res.data);
        setFeedback({ type: 'success', message: 'Content restored to defaults.' });
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Error resetting.' });
    } finally {
      setSaving(false);
    }
  };

  // Helper updates for nested fields
  const updateHero = (field, val) => {
    setContent(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: val }
    }));
  };

  const updateHeroStat = (index, field, val) => {
    setContent(prev => {
      const stats = [...(prev.hero?.stats || [])];
      stats[index] = { ...stats[index], [field]: val };
      return {
        ...prev,
        hero: { ...prev.hero, stats }
      };
    });
  };

  const updateWhyUs = (field, val) => {
    setContent(prev => ({
      ...prev,
      whyUs: { ...prev.whyUs, [field]: val }
    }));
  };

  const updateWhyFeature = (index, field, val) => {
    setContent(prev => {
      const features = [...(prev.whyUs?.features || [])];
      features[index] = { ...features[index], [field]: val };
      return {
        ...prev,
        whyUs: { ...prev.whyUs, features }
      };
    });
  };

  const updateCta = (field, val) => {
    setContent(prev => ({
      ...prev,
      ctaBanner: { ...prev.ctaBanner, [field]: val }
    }));
  };

  const openAddTestimonial = () => {
    setTempTestimonial({
      name: '',
      role: '',
      course: '',
      text: '',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20young%20Nigerian%20smiling%20African%20tech%20headshot&width=100&height=100&seq=t4&orientation=squarish',
      rating: 5
    });
    setEditingTestimonialIndex(null);
    setTestimonialModal(true);
  };

  const openEditTestimonial = (idx) => {
    const item = content?.testimonials?.items?.[idx];
    if (item) {
      setTempTestimonial({ ...item });
      setEditingTestimonialIndex(idx);
      setTestimonialModal(true);
    }
  };

  const saveTestimonial = (e) => {
    e.preventDefault();
    setContent(prev => {
      const currentItems = [...(prev.testimonials?.items || [])];
      if (editingTestimonialIndex !== null) {
        currentItems[editingTestimonialIndex] = tempTestimonial;
      } else {
        currentItems.push({ ...tempTestimonial, id: String(Date.now()) });
      }
      return {
        ...prev,
        testimonials: { ...prev.testimonials, items: currentItems }
      };
    });
    setTestimonialModal(false);
  };

  const removeTestimonial = (idx) => {
    setContent(prev => {
      const currentItems = [...(prev.testimonials?.items || [])];
      currentItems.splice(idx, 1);
      return {
        ...prev,
        testimonials: { ...prev.testimonials, items: currentItems }
      };
    });
  };

  if (loading || !content) {
    return (
      <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-xs text-slate-500">Loading home page content manager...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Header & Save Controls */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-900">Home Page Content Management (CMS)</h2>
            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
              Live Synchronized
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Customize promotional text, headlines, value propositions, and student testimonials rendered on the public home page.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
          >
            <i className="ri-refresh-line mr-1" /> Reset Defaults
          </button>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-external-link-line" /> View Public Page
          </a>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-md cursor-pointer"
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-3-line" />
                Save & Publish
              </>
            )}
          </button>
        </div>
      </div>

      {feedback.message && (
        <div
          className={`p-4 rounded-2xl text-xs flex items-center gap-2 border ${
            feedback.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-rose-50 text-rose-800 border-rose-200'
          }`}
        >
          <i
            className={`text-base ${
              feedback.type === 'success' ? 'ri-checkbox-circle-line text-emerald-600' : 'ri-error-warning-line text-rose-600'
            }`}
          />
          {feedback.message}
        </div>
      )}

      {/* Section Switcher Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-1 overflow-x-auto">
        {[
          { id: 'hero', label: '🚀 Hero & Header Banner' },
          { id: 'whyUs', label: '💎 Why LearnFlow & Features' },
          { id: 'testimonials', label: '💬 Student Testimonials' },
          { id: 'ctaBanner', label: '🎯 Call To Action Banner' },
        ].map((sec) => (
          <button
            key={sec.id}
            type="button"
            onClick={() => setActiveSection(sec.id)}
            className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
              activeSection === sec.id
                ? 'bg-teal-600 text-white shadow-xs'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            {sec.label}
          </button>
        ))}
      </div>

      {/* SECTION 1: HERO */}
      {activeSection === 'hero' && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-xs space-y-6">
          <div>
            <h3 className="font-bold text-slate-900 text-sm">Hero Headline & Value Narrative</h3>
            <p className="text-xs text-slate-400 mt-0.5">Top-of-fold introduction seen by all visitors</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Top Badge Pill Text</label>
              <input
                type="text"
                value={content.hero?.badgeText || ''}
                onChange={(e) => updateHero('badgeText', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Hero Background Image URL</label>
              <input
                type="text"
                value={content.hero?.backgroundImage || ''}
                onChange={(e) => updateHero('backgroundImage', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Main Heading (Prefix)</label>
              <input
                type="text"
                value={content.hero?.title || ''}
                onChange={(e) => updateHero('title', e.target.value)}
                placeholder="Learn the Skills That"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Gradient Highlighted Headline</label>
              <input
                type="text"
                value={content.hero?.titleHighlight || ''}
                onChange={(e) => updateHero('titleHighlight', e.target.value)}
                placeholder="Naija Demands Today"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none text-teal-700 font-bold"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Hero Subtitle & Description</label>
              <textarea
                value={content.hero?.description || ''}
                onChange={(e) => updateHero('description', e.target.value)}
                rows={3}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none leading-relaxed"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Primary CTA Button Text</label>
              <input
                type="text"
                value={content.hero?.primaryCtaText || ''}
                onChange={(e) => updateHero('primaryCtaText', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Secondary CTA Button Text</label>
              <input
                type="text"
                value={content.hero?.secondaryCtaText || ''}
                onChange={(e) => updateHero('secondaryCtaText', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Hero Stats */}
          <div className="pt-4 border-t border-slate-100">
            <h4 className="font-bold text-slate-900 text-xs mb-3">Hero Trust Metric Badges (4 counters)</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(content.hero?.stats || []).map((st, i) => (
                <div key={i} className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <div>
                    <label className="text-[10px] text-slate-400 font-semibold block">Counter Value</label>
                    <input
                      type="text"
                      value={st.value}
                      onChange={(e) => updateHeroStat(i, 'value', e.target.value)}
                      className="w-full px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400 font-semibold block">Label</label>
                    <input
                      type="text"
                      value={st.label}
                      onChange={(e) => updateHeroStat(i, 'label', e.target.value)}
                      className="w-full px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 2: WHY US */}
      {activeSection === 'whyUs' && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-xs space-y-6">
          <div>
            <h3 className="font-bold text-slate-900 text-sm">Why LearnFlow / Value Proposition</h3>
            <p className="text-xs text-slate-400 mt-0.5">Highlights explaining the value tailored to Nigerian learners</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Section Tagline</label>
              <input
                type="text"
                value={content.whyUs?.badgeText || ''}
                onChange={(e) => updateWhyUs('badgeText', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Section Heading</label>
              <input
                type="text"
                value={content.whyUs?.title || ''}
                onChange={(e) => updateWhyUs('title', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Narrative Description</label>
              <textarea
                value={content.whyUs?.description || ''}
                onChange={(e) => updateWhyUs('description', e.target.value)}
                rows={3}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none leading-relaxed"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Satisfaction Rate Badge</label>
              <input
                type="text"
                value={content.whyUs?.satisfactionRate || ''}
                onChange={(e) => updateWhyUs('satisfactionRate', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Rating Score Badge</label>
              <input
                type="text"
                value={content.whyUs?.ratingScore || ''}
                onChange={(e) => updateWhyUs('ratingScore', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>
          </div>

          {/* 4 Feature Items */}
          <div className="pt-4 border-t border-slate-100">
            <h4 className="font-bold text-slate-900 text-xs mb-3">Key Feature Pillars</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(content.whyUs?.features || []).map((feat, i) => (
                <div key={i} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={feat.icon}
                      onChange={(e) => updateWhyFeature(i, 'icon', e.target.value)}
                      placeholder="Icon class"
                      className="w-28 px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs"
                    />
                    <input
                      type="text"
                      value={feat.title}
                      onChange={(e) => updateWhyFeature(i, 'title', e.target.value)}
                      placeholder="Feature title"
                      className="flex-1 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                    />
                  </div>
                  <textarea
                    value={feat.desc}
                    onChange={(e) => updateWhyFeature(i, 'desc', e.target.value)}
                    placeholder="Short description"
                    rows={2}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs leading-relaxed"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 3: TESTIMONIALS */}
      {activeSection === 'testimonials' && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-xs space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Verified Student Testimonials</h3>
              <p className="text-xs text-slate-400 mt-0.5">Showcase testimonials from successful Nigerian graduates</p>
            </div>
            <button
              type="button"
              onClick={openAddTestimonial}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer shadow-xs"
            >
              <i className="ri-add-line" /> Add Testimonial
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(content.testimonials?.items || []).map((t, idx) => (
              <div
                key={t.id || idx}
                className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 flex flex-col justify-between space-y-4 hover:border-teal-300 transition-all"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-amber-400">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i key={s} className="ri-star-fill text-xs" />
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditTestimonial(idx)}
                        className="p-1 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                        title="Edit testimonial"
                      >
                        <i className="ri-edit-line text-xs" />
                      </button>
                      <button
                        onClick={() => removeTestimonial(idx)}
                        className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                        title="Delete testimonial"
                      >
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed italic line-clamp-4">&ldquo;{t.text}&rdquo;</p>
                </div>

                <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
                  <img src={t.avatar} alt={t.name} className="w-9 h-9 rounded-full object-cover border border-slate-200" />
                  <div>
                    <h5 className="font-bold text-xs text-slate-900">{t.name}</h5>
                    <p className="text-[11px] text-slate-400">{t.role} • {t.course}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 4: CTA BANNER */}
      {activeSection === 'ctaBanner' && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-xs space-y-6">
          <div>
            <h3 className="font-bold text-slate-900 text-sm">Bottom Call To Action (CTA) Banner</h3>
            <p className="text-xs text-slate-400 mt-0.5">The high-conversion closing banner across the page footer</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Banner Headline</label>
              <input
                type="text"
                value={content.ctaBanner?.title || ''}
                onChange={(e) => updateCta('title', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none font-bold"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Banner Description</label>
              <textarea
                value={content.ctaBanner?.description || ''}
                onChange={(e) => updateCta('description', e.target.value)}
                rows={3}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none leading-relaxed"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Instructor Faculty Highlight</label>
              <input
                type="text"
                value={content.ctaBanner?.instructorHighlight || ''}
                onChange={(e) => updateCta('instructorHighlight', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none text-teal-700 font-semibold"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Primary Button Text</label>
              <input
                type="text"
                value={content.ctaBanner?.primaryButtonText || ''}
                onChange={(e) => updateCta('primaryButtonText', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Secondary Button Text</label>
              <input
                type="text"
                value={content.ctaBanner?.secondaryButtonText || ''}
                onChange={(e) => updateCta('secondaryButtonText', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Testimonial Modal */}
      {testimonialModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <h3 className="font-bold text-base text-slate-900">
                {editingTestimonialIndex !== null ? 'Edit Student Testimonial' : 'Add New Testimonial'}
              </h3>
              <button
                onClick={() => setTestimonialModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={saveTestimonial} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Student Full Name</label>
                <input
                  type="text"
                  value={tempTestimonial.name}
                  onChange={(e) => setTempTestimonial({ ...tempTestimonial, name: e.target.value })}
                  placeholder="e.g. Chukwuma Obi"
                  required
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Role / Company</label>
                  <input
                    type="text"
                    value={tempTestimonial.role}
                    onChange={(e) => setTempTestimonial({ ...tempTestimonial, role: e.target.value })}
                    placeholder="e.g. Backend Engineer at Kuda"
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Course Studied</label>
                  <input
                    type="text"
                    value={tempTestimonial.course}
                    onChange={(e) => setTempTestimonial({ ...tempTestimonial, course: e.target.value })}
                    placeholder="e.g. Full-Stack Dev"
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Avatar Photo URL</label>
                <input
                  type="text"
                  value={tempTestimonial.avatar}
                  onChange={(e) => setTempTestimonial({ ...tempTestimonial, avatar: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Testimonial Quote</label>
                <textarea
                  value={tempTestimonial.text}
                  onChange={(e) => setTempTestimonial({ ...tempTestimonial, text: e.target.value })}
                  rows={4}
                  required
                  placeholder="What positive impact did LearnHub have on the student's career?"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none leading-relaxed"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setTestimonialModal(false)}
                  className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-xl transition-all shadow-md cursor-pointer"
                >
                  Save Testimonial
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
